import { isAxiosError, AxiosResponse } from 'axios';
import telemetryService from '|/app/services/telemetry/telemetry-service';
import axiosInstance from '|/helpers/axios';
import { CaseDefaults } from './verification-api.types';
import getElapsedTimeInSeconds from '../utils/getElapsedTimeInSeconds';

const COMPLETE_DOCUMENT_UPLOAD_ERROR =
  'Your appointment is scheduled, but we had an unexpected error loading the next page.';

const baseUrl = `${process.env.CUSTOMER_VERIFICATIONS_API_URL}/api`;

export const getCaseDefaults = (abortSignal?: AbortSignal) => {
  const startTime = performance.now();

  telemetryService.trackEvent({
    name: 'verifications-api:getCaseDefaults:start'
  });

  return axiosInstance
    .get<never, AxiosResponse<CaseDefaults>>(`${baseUrl}/v1/Case/default`, { signal: abortSignal })
    .then(res => {
      const elapsedTimeInSeconds = getElapsedTimeInSeconds(startTime);

      telemetryService.trackEvent({
        name: 'verifications-api:getCaseDefaults:success',
        context: {
          elapsedTimeInSeconds: elapsedTimeInSeconds
        }
      });

      return res.data;
    })
    .catch((err: unknown) => {
      const elapsedTimeInSeconds = getElapsedTimeInSeconds(startTime);

      telemetryService.trackError({
        error: err,
        name: 'verifications-api:getCaseDefaults:error',
        context: {
          elapsedTimeInSeconds: elapsedTimeInSeconds
        }
      });

      throw new Error('Failed to get case defaults.', { cause: err });
    });
};

export const completeDocumentUpload = (caseId: number, abortSignal?: AbortSignal) => {
  const startTime = performance.now();

  telemetryService.trackEvent({
    name: 'verifications-api:completeDocumentUpload:start'
  });

  return axiosInstance
    .post<never, AxiosResponse<never, never>, never>(
      `${baseUrl}/v1/case/${caseId}/document-upload/complete`,
      undefined,
      { signal: abortSignal }
    )
    .then(() => {
      telemetryService.trackEvent({
        name: 'verifications-api:completeDocumentUpload:success',
        context: {
          elapsedTimeInSeconds: getElapsedTimeInSeconds(startTime)
        }
      });
    })
    .catch((err: unknown) => {
      telemetryService.trackError({
        error: err,
        name: 'verifications-api:completeDocumentUpload:error',
        context: {
          elapsedTimeInSeconds: getElapsedTimeInSeconds(startTime),
          ...(isAxiosError(err) && err.response && { response: err.response.data })
        }
      });

      throw new Error(COMPLETE_DOCUMENT_UPLOAD_ERROR, { cause: err });
    });
};
